import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { apiClient } from '../../services/api';
import { toast } from 'react-toastify';
import NotificationRecipientFields from './NotificationRecipientFields';
import {
  channelUsesEmail,
  channelUsesSms,
  channelUsesWhatsApp,
  NOTIFICATION_CHANNEL_OPTIONS,
  type NotificationChannelSelection,
} from '../../utils/notificationChannels';

interface ManagerAlertModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  campaignId?: string;
  supervisorId?: string;
  defaultRecipient?: string;
}

const ManagerAlertModal: React.FC<ManagerAlertModalProps> = ({
  open,
  onClose,
  date,
  campaignId,
  supervisorId,
  defaultRecipient,
}) => {
  const [channel, setChannel] = useState<NotificationChannelSelection>('email');
  const [recipients, setRecipients] = useState(defaultRecipient || '');
  const [smsRecipients, setSmsRecipients] = useState('');
  const [defaultPhone, setDefaultPhone] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRecipients(defaultRecipient || '');
    setSmsRecipients('');
    setPreview(null);
    Promise.all([
      apiClient.notificationSettings.getStatus(),
      apiClient.userNotificationPreferences.get().catch(() => null),
    ]).then(([statusRes, prefsRes]) => {
      setDeliveryStatus(statusRes.data.data);
      const phone = prefsRes?.data?.data?.phoneNumber;
      if (phone) {
        setDefaultPhone(phone);
        setSmsRecipients(phone);
      }
    }).catch(() => setDeliveryStatus(null));
  }, [open, defaultRecipient]);

  const buildPayload = () => ({
    date,
    campaignId: campaignId || undefined,
    supervisorId: supervisorId || undefined,
    channel,
    recipients: recipients.split(',').map((r) => r.trim()).filter(Boolean),
    smsRecipients: smsRecipients.split(',').map((r) => r.trim()).filter(Boolean),
  });

  const validateRecipients = () => {
    const payload = buildPayload();
    if (channelUsesEmail(channel) && payload.recipients.length === 0) {
      toast.error('Select or enter at least one email recipient');
      return false;
    }
    if ((channelUsesSms(channel) || channelUsesWhatsApp(channel)) && payload.smsRecipients.length === 0) {
      toast.error('Select or enter at least one phone number');
      return false;
    }
    if (channelUsesSms(channel) && deliveryStatus && !deliveryStatus.smsReady) {
      toast.error('SMS is not ready. Enable SMS and configure Twilio in Notification Settings.');
      return false;
    }
    if (channelUsesWhatsApp(channel) && deliveryStatus && !deliveryStatus.whatsappReady) {
      toast.error('WhatsApp is not ready. Enable WhatsApp and configure the Twilio sandbox sender in Notification Settings.');
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    if (!validateRecipients()) return;
    try {
      setLoading(true);
      const response = await apiClient.operations.previewAlert(buildPayload());
      setPreview(response.data.data);
      if (response.data.data?.deliveryStatus) {
        setDeliveryStatus(response.data.data.deliveryStatus);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to generate alert preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!validateRecipients()) return;
    try {
      setLoading(true);
      const response = await apiClient.operations.sendManagerAlert(buildPayload());
      const results = response.data.data?.results || [];
      const failed = results.filter((r: any) => r.status === 'failed');
      const skipped = results.filter((r: any) => r.status === 'skipped');

      if (failed.length) {
        toast.warning(`${response.data.message || 'Sent with failures'}: ${failed.map((r: any) => r.error).join('; ')}`);
      } else if (skipped.length) {
        toast.warning(response.data.message || 'Delivery skipped — check Notification Settings');
      } else {
        toast.success(response.data.message || 'Manager alert sent');
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to send manager alert');
    } finally {
      setLoading(false);
    }
  };

  const smsReady = deliveryStatus?.smsReady;
  const whatsappReady = deliveryStatus?.whatsappReady;
  const showSmsWarning = channelUsesSms(channel) && deliveryStatus && !smsReady;
  const showWhatsAppWarning = channelUsesWhatsApp(channel) && deliveryStatus && !whatsappReady;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send Manager Alert</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          Short operational alert for executives and managers — exceptions only.
        </Alert>
        <Grid container spacing={2}>
          {showSmsWarning && (
            <Grid item xs={12}>
              <Alert severity="warning">
                SMS is not ready. Configure Twilio under Notification Settings (or TWILIO_* in .env.development) and enable SMS.
              </Alert>
            </Grid>
          )}
          {showWhatsAppWarning && (
            <Grid item xs={12}>
              <Alert severity="warning">
                WhatsApp is not ready. Configure Twilio under Notification Settings, set the sandbox sender
                (whatsapp:+14155238886), and enable WhatsApp. Recipients must join the Twilio sandbox first.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select value={channel} label="Channel" onChange={(e) => setChannel(e.target.value as NotificationChannelSelection)}>
                {NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <NotificationRecipientFields
              channel={channel}
              emailRecipients={recipients}
              smsRecipients={smsRecipients}
              onEmailRecipientsChange={setRecipients}
              onSmsRecipientsChange={setSmsRecipients}
              defaultEmail={defaultRecipient}
              defaultPhone={defaultPhone}
            />
          </Grid>
          {preview && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>Preview Alert</Alert>
              <Box sx={{ bgcolor: '#f7f9fc', p: 2, borderRadius: 1, maxHeight: 480, overflow: 'auto' }}>
                {channelUsesEmail(channel) && (
                  <>
                    <Typography variant="subtitle2">Email Subject</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{preview.email?.subject}</Typography>
                    <Typography variant="subtitle2">Email Body</Typography>
                    <Box
                      sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}
                      dangerouslySetInnerHTML={{ __html: preview.email?.body || '' }}
                    />
                  </>
                )}
                {(channelUsesSms(channel) || channelUsesWhatsApp(channel)) && (
                  <>
                    {channelUsesSms(channel) && (
                      <>
                        <Typography variant="subtitle2">SMS → {smsRecipients || '—'}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: channelUsesWhatsApp(channel) ? 2 : 0 }}>{preview.sms?.body}</Typography>
                      </>
                    )}
                    {channelUsesWhatsApp(channel) && (
                      <>
                        <Typography variant="subtitle2">WhatsApp → {smsRecipients || '—'}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{preview.sms?.body}</Typography>
                      </>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {preview.sms?.body?.length || 0} characters — link opens the full alert (same as email) with PDF download
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handlePreview} disabled={loading}>Preview Alert</Button>
        <Button variant="contained" onClick={handleSend} disabled={loading || showSmsWarning || showWhatsAppWarning}>
          {loading ? 'Sending...' : 'Send Manager Alert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerAlertModal;
